import { formatRawAmount, formatBlendAmount } from "./formatting.js";
import { isAddedSugars, getIndentClass, getDietaryIndentClass } from "./nutrients.js";
import { sortByAmountDesc } from "./sorting.js";

export const NO_DV_SYMBOL = "†";

function findByDvKey(values, line) {
  if (!values || !line?.dv_key) return null;
  return values.find((v) => v.key === line.dv_key);
}

function findField({ calculatedValues, source, line, field }) {
  const calculated = findByDvKey(calculatedValues?.[source], line);
  return calculated?.[field];
}

function findMultiColumnDv({ calculatedValues, source, line, ageCategory }) {
  const calculated = findByDvKey(calculatedValues?.[source], line);
  return calculated?.formatted_dvs?.[ageCategory];
}

export const CalculatedValues = {
  getAmount(calculatedValues, line) {
    if (!line.dv_key) return formatRawAmount(line);
    return findField({ calculatedValues, source: "dv_lines", line, field: "formatted_amount" });
  },

  getDv(calculatedValues, line) {
    return findField({ calculatedValues, source: "dv_lines", line, field: "formatted_dv" });
  },

  getMultiColumnDv({ calculatedValues, line, ageCategory }) {
    return findMultiColumnDv({ calculatedValues, source: "dv_lines", line, ageCategory });
  },

  getBlendAmount(blend) {
    return formatBlendAmount(blend);
  },

  getDietaryDv(calculatedValues, line) {
    return (
      findField({ calculatedValues, source: "dietary_lines", line, field: "formatted_dv" }) || ""
    );
  },

  getDietaryAmount(calculatedValues, line) {
    return findField({
      calculatedValues,
      source: "dietary_lines",
      line,
      field: "formatted_amount",
    });
  },

  getDietaryMultiColumnDv({ calculatedValues, line, ageCategory }) {
    return (
      findMultiColumnDv({ calculatedValues, source: "dietary_lines", line, ageCategory }) || ""
    );
  },

  getVisibleDvLines(store) {
    const visibleKeys = new Set((store.calculatedValues?.dv_lines || []).map((calc) => calc.key));

    const filtered = [];
    store.dvLines.forEach((line, originalIndex) => {
      if (line.name && visibleKeys.has(line.dv_key)) {
        filtered.push({ line, originalIndex });
      }
    });
    return filtered;
  },

  getFilteredDietaryLines(store) {
    const visibleKeys = new Set(
      (store.calculatedValues?.dietary_lines || []).map((calc) => calc.key)
    );

    return store.dietaryLines.filter((line) => {
      if (!line.amount) return false;
      return visibleKeys.has(line.dv_key);
    });
  },

  getVisibleNoDvLines(store) {
    return sortByAmountDesc(
      store.linesWithoutDv.filter((line) => line.name),
      "amount",
      "unit"
    );
  },

  getVisibleBlends(store) {
    return sortByAmountDesc(
      store.blends.filter((blend) => blend.name),
      "amount",
      "unit"
    );
  },

  shouldShowNoDvFootnote(store) {
    const hasVisibleNoDvLines = store.linesWithoutDv.some((l) => l.name);

    const hasVisibleBlends = store.blends.some((b) => {
      if (!b.name) return false;
      if (!b.hide_dagger) return true;

      if (b.display_mode === "inline") return false;
      return b.constituents?.some((c) => c.name && !c.hide_dagger);
    });

    return hasVisibleNoDvLines || hasVisibleBlends || store.calculatedValues?.show_dv_footnote;
  },

  hasAnyEstablishedDv(store) {
    const dietaryLines = this.getFilteredDietaryLines(store);
    const hasDietaryWithDv = dietaryLines.some((l) => {
      const dv = this.getDietaryDv(store.calculatedValues, l);
      return dv && dv !== "" && dv !== NO_DV_SYMBOL;
    });
    if (hasDietaryWithDv) return true;

    return store.dvLines.some((l) => l.name);
  },

  getDietaryDividerClass({ store, line, index, filteredDietaryLines }) {
    const isLastDietaryLine = index === filteredDietaryLines.length - 1;
    const hasFollowingGroupA = CalculatedValues.getVisibleDvLines(store).length > 0;
    const hasGroupB =
      CalculatedValues.getVisibleNoDvLines(store).length > 0 ||
      CalculatedValues.getVisibleBlends(store).length > 0;

    if (isLastDietaryLine && !hasFollowingGroupA && !hasGroupB) {
      return "";
    }

    // 21 CFR 101.36(e)(6)(ii) requires a heavy bar after the last paragraph (b)(2)(i) row.
    if (isLastDietaryLine && !hasFollowingGroupA && hasGroupB) {
      return "SupplementFacts-Bar-Thick";
    }

    // 21 CFR 101.36(e)(5) requires a hairline between dietary ingredient rows.
    const currentIndent = getDietaryIndentClass(line, store);
    if (currentIndent === "SupplementFacts-Indent-Double") {
      return "SupplementFacts-Bar-Hairline-Indent-Double";
    } else if (currentIndent === "SupplementFacts-Indent") {
      return "SupplementFacts-Bar-Hairline-Indent";
    }

    return "SupplementFacts-Bar-Hairline";
  },

  getDvLineDividerClass({ store, line, index, filteredDvLines }) {
    const isLastDvLine = index === filteredDvLines.length - 1;
    const hasGroupB =
      CalculatedValues.getVisibleNoDvLines(store).length > 0 ||
      CalculatedValues.getVisibleBlends(store).length > 0;

    // 21 CFR 101.36(e)(6)(ii) requires a heavy bar after the last paragraph (b)(2)(i) row.
    if (isLastDvLine && hasGroupB) {
      return "SupplementFacts-Bar-Thick";
    }

    if (isLastDvLine) {
      return "";
    }

    if (isAddedSugars(line)) return "SupplementFacts-Bar-Partial";

    const currentIndent = getIndentClass(
      line,
      store.subNutrientParents ? Object.keys(store.subNutrientParents) : []
    );

    const nextLine = filteredDvLines[index + 1];
    if (nextLine && nextLine.line) {
      const nextIndent = getIndentClass(
        nextLine.line,
        store.subNutrientParents ? Object.keys(store.subNutrientParents) : []
      );

      if (currentIndent && nextIndent) {
        if (
          currentIndent === "SupplementFacts-Indent-Double" ||
          nextIndent === "SupplementFacts-Indent-Double"
        ) {
          if (
            currentIndent === "SupplementFacts-Indent-Double" &&
            nextIndent === "SupplementFacts-Indent-Double"
          )
            return "SupplementFacts-Bar-Hairline-Indent-Double";

          return "SupplementFacts-Bar-Hairline-Indent";
        }
        return "SupplementFacts-Bar-Hairline-Indent";
      }
    }

    return "SupplementFacts-Bar-Hairline";
  },
};
